"use client";

import React from "react";

export interface CtlBlueprintBlockProps {
    tag?: string;
    title?: string;
    description?: string;
    
    // Left Box (System Scale)
    leftTag?: string;
    leftTitle?: string;
    leftDesc?: string;
    leftFooter?: string;
    
    // Middle Box (Project Deployments)
    centerTag?: string;
    centerTitle?: string;
    centerSub?: string;
    centerStats?: string[];
    centerFooter?: string;
    
    // Right Box (Consultancies)
    rightTag?: string;
    consultancies?: { title: string; subtitle: string }[];
    rightFooter?: string;
    
    // Bottom Section
    systemLabel?: string;
    offices?: string[];
}

export default function CtlBlueprintBlock({
    tag = "02 / OPERATIONAL SPECIFICATION",
    title = "Infrastructure Grid & Project Footprint",
    description = "A breakdown of our legacy system footprint. Our engineering models support major projects across airports, banking networks, and high-capacity data centers.",
    
    leftTag = "System Scale",
    leftTitle = "Global Partnerships",
    leftDesc = "We have established a successful alliance with 2000+ clients and empower 5 Million users with technology driven growth.",
    leftFooter = "25+ Years of Experience",
    
    centerTag = "Project Deployments",
    centerTitle = "CIAL DATA CENTER BUILD",
    centerSub = "Design, Construction & Commissioning",
    centerStats = ["Federal Bank: 800 Branches", "SIB: 700 Branches"],
    centerFooter = "Locations: Mumbai, Bengaluru, Kochi",
    
    rightTag = "Consultancies",
    consultancies = [
        { title: "Kannur Airport", subtitle: "IT & ELV Consultant" },
        { title: "HAL Bangalore", subtitle: "Data Center Build" }
    ],
    rightFooter = "ESTABLISHED 1994",
    
    systemLabel = "TECHNOLOGY DRIVEN INFRASTRUCTURE: MULTI-OFFICE PAN-INDIA OPERATIONS",
    offices = ["MUMBAI", "BENGALURU", "KOCHI"]
}: CtlBlueprintBlockProps) {
    return (
        <section className="py-28 px-6 md:px-12 lg:px-24 bg-white border-b border-[#EAEAEA] text-[#444] font-sans antialiased w-full">
            <div className="container mx-auto">
                <div className="max-w-3xl mb-16">
                    {tag && (
                        <span className="text-xs font-mono text-[#44AA52] tracking-widest block mb-2 uppercase font-semibold">
                            {tag}
                        </span>
                    )}
                    {title && (
                        <h2 className="text-4xl font-light text-[#1A1A1A] leading-tight">
                            {title}
                        </h2>
                    )}
                    {description && (
                        <p className="text-zinc-500 text-sm mt-3 font-light max-w-xl">
                            {description}
                        </p>
                    )}
                </div>

                {/* The Grid Blueprint Card */}
                <div className="w-full bg-[#FAFBF9] border border-[#E5ECE5] rounded-2xl p-6 md:p-8 lg:p-12 relative overflow-hidden shadow-sm">
                    
                    {/* Ruler Markings */}
                    <div className="absolute top-0 left-12 right-12 h-6 border-b border-[#EBEBEB] flex justify-between items-center text-[9px] font-mono text-zinc-400 select-none pointer-events-none">
                        <span>0px</span>
                        <span>305px</span>
                        <span>626px</span>
                        <span>947px</span>
                        <span>1268px</span>
                        <span>1440px</span>
                    </div>
                    <div className="absolute left-0 top-12 bottom-12 w-6 border-r border-[#EBEBEB] flex flex-col justify-between items-center text-[9px] font-mono text-zinc-400 py-2 select-none pointer-events-none">
                        <span>Y:0</span>
                        <span>Y:200</span>
                        <span>Y:400</span>
                        <span>Y:600</span>
                    </div>

                    {/* Interactive Blueprint Mockup */}
                    <div className="pt-8 grid grid-cols-1 md:grid-cols-12 gap-6 relative z-10">
                        
                        {/* Left block representation */}
                        <div className="md:col-span-4 border border-dashed border-[#44aa52]/30 bg-[#44aa52]/3 rounded p-6 relative flex flex-col justify-between h-80">
                            {leftTag && (
                                <div className="absolute -top-3 left-4 bg-[#FAFBF9] px-2 py-0.5 rounded border border-[#E5ECE5] text-[8px] font-mono text-[#44AA52]">
                                    {leftTag}
                                </div>
                            )}
                            <div className="space-y-4">
                                <span className="text-[10px] font-mono text-[#44AA52] tracking-widest block font-semibold">// CORE STATISTICS</span>
                                {leftTitle && <h3 className="text-2xl font-light text-[#1A1A1A] italic">{leftTitle}</h3>}
                                {leftDesc && <p className="text-zinc-650 text-[11px] leading-relaxed font-light">{leftDesc}</p>}
                            </div>
                            {leftFooter && (
                                <div className="text-[8px] font-mono text-zinc-450 font-medium">
                                    {leftFooter}
                                </div>
                            )}
                        </div>

                        {/* Middle block representation */}
                        <div className="md:col-span-5 border border-dashed border-[#44aa52]/30 bg-[#44aa52]/3 rounded p-6 relative flex flex-col justify-between h-80">
                            {centerTag && (
                                <div className="absolute -top-3 left-4 bg-[#FAFBF9] px-2 py-0.5 rounded border border-[#E5ECE5] text-[8px] font-mono text-[#44AA52]">
                                    {centerTag}
                                </div>
                            )}
                            <div className="space-y-4">
                                <div className="h-28 rounded bg-white border border-[#E5ECE5] flex flex-col items-center justify-center font-mono text-[9px] text-[#44AA52] relative overflow-hidden p-4 space-y-2 shadow-xs">
                                    <div className="absolute inset-0 bg-[linear-gradient(to_right,#44aa5208_1px,transparent_1px),linear-gradient(to_bottom,#44aa5208_1px,transparent_1px)] bg-[size:16px_16px] pointer-events-none" />
                                    {centerTitle && <div className="text-[10px] font-bold text-[#44AA52]">{centerTitle}</div>}
                                    {centerSub && <div className="text-[8px] text-zinc-400">{centerSub}</div>}
                                </div>
                                {centerStats && centerStats.length >= 2 && (
                                    <div className="flex justify-between items-center text-[10px] font-mono">
                                        <span className="text-zinc-550">{centerStats[0]}</span>
                                        <span className="text-[#44AA52] font-semibold">{centerStats[1]}</span>
                                    </div>
                                )}
                            </div>
                            {centerFooter && (
                                <div className="text-[8px] font-mono text-zinc-450">
                                    {centerFooter}
                                </div>
                            )}
                        </div>

                        {/* Right block representation */}
                        <div className="md:col-span-3 border border-dashed border-[#44aa52]/30 bg-[#44aa52]/3 rounded p-6 relative flex flex-col justify-between h-80">
                            {rightTag && (
                                <div className="absolute -top-3 left-4 bg-[#FAFBF9] px-2 py-0.5 rounded border border-[#E5ECE5] text-[8px] font-mono text-[#44AA52]">
                                    {rightTag}
                                </div>
                            )}
                            <div className="space-y-4">
                                {consultancies && consultancies.map((c, idx) => (
                                    <div key={idx} className="p-3 bg-white border border-[#E5ECE5] rounded text-center shadow-xs">
                                        <span className="text-[10px] font-mono text-[#44AA52] block font-bold">{c.title}</span>
                                        <span className="text-[8px] font-mono text-zinc-400 block uppercase">{c.subtitle}</span>
                                    </div>
                                ))}
                            </div>
                            {rightFooter && (
                                <div className="w-full py-2 border border-[#E5ECE5] text-[#44AA52] bg-[#44aa52]/5 font-mono text-[9px] text-center uppercase tracking-wider font-semibold rounded">
                                    {rightFooter}
                                </div>
                            )}
                        </div>

                    </div>

                    {/* Extra Layout spec details underneath */}
                    <div className="mt-8 border-t border-[#EAEAEA] pt-6 flex flex-wrap justify-between items-center text-[10px] font-mono text-zinc-400 gap-4">
                        {systemLabel && <div>{systemLabel}</div>}
                        {offices && offices.length > 0 && (
                            <div className="flex gap-4">
                                <span>OFFICES:</span>
                                {offices.map((o, idx) => (
                                    <span key={idx}>{o}</span>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </section>
    );
}
